/**
 * Finishes by space: the floor texture sets and the HDRIs a kind of space
 * (garage, workshop, laundry room …) may be rendered with, each entry with
 * its own settings — kept in the props library's manifest (`finishes`, saved
 * through Prop Studio's back end like the catalogue) and edited in the
 * Photoreal window. Stage draws one floor entry and one HDRI entry for the
 * plan's space type with the staging's seed (`pickFinish`) and the plan
 * keeps the pick (`plan.finish`); at export the pick fills what the
 * Photoreal settings leave open — no floor set chosen, no HDRI chosen — so
 * a choice made in the window always stands (`applyFinish`).
 */

import type { PlanFinish } from '../../../calculations/plan/floorPlan';
import { rng } from '../../../calculations/staging/stage';
import { HDRI_NAME, PhotoRenderSettings, RANGES, TINT_HEX, TexturePlacement, texturePlacement } from './photoRender';

export type FloorFinish = TexturePlacement;
export type HdriFinish = NonNullable<PlanFinish['hdri']>;
export interface SpaceFinishes { floor: FloorFinish[]; hdri: HdriFinish[] }
/** By space type (presets.ts SPACE_TYPES ids). */
export type FinishPools = Record<string, SpaceFinishes>;

/** A space type key as the back ends take it (server.js SPACE_ID). */
export const SPACE_ID = /^[a-z0-9-]{1,40}$/;
/** The most entries a space holds of each kind. */
export const FINISH_MAX = 20;

const inRange = (v: unknown, range: keyof typeof RANGES): v is number => {
  const [lo, hi] = RANGES[range];
  return typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
};

/** A floor entry as saved (a texture placement; rotation, brightness and the tint factory when an older entry lacks them), or null when it is not one. */
export function floorFinish(raw: unknown): FloorFinish | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  if (!inRange(e.scale, 'textures.scale') || !inRange(e.roughness, 'textures.roughness') || !inRange(e.relief, 'textures.relief')) return null;
  if (e.rotation !== undefined && !inRange(e.rotation, 'textures.rotation')) return null;
  if (e.brightness !== undefined && !inRange(e.brightness, 'textures.brightness')) return null;
  if (e.tintStrength !== undefined && !inRange(e.tintStrength, 'textures.tintStrength')) return null;
  if (e.tint !== undefined && (typeof e.tint !== 'string' || !TINT_HEX.test(e.tint.toLowerCase()))) return null;
  return texturePlacement(e);
}

/** An HDRI entry as saved, or null when it is not one. */
export function hdriFinish(raw: unknown): HdriFinish | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  if (typeof e.file !== 'string' || !HDRI_NAME.test(e.file)) return null;
  if (!inRange(e.strength, 'light.hdriStrength') || !inRange(e.rotation, 'light.hdriRotation')) return null;
  return { file: e.file, strength: e.strength, rotation: e.rotation };
}

/** The pools as the library sends them: bad entries dropped, spaces with nothing left out. */
export function finishPools(raw: unknown): FinishPools {
  const out: FinishPools = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [space, pool] of Object.entries(raw as Record<string, unknown>)) {
    if (!SPACE_ID.test(space) || !pool || typeof pool !== 'object') continue;
    const p = pool as { floor?: unknown; hdri?: unknown };
    const floor = (Array.isArray(p.floor) ? p.floor : []).map(floorFinish).filter((e): e is FloorFinish => !!e).slice(0, FINISH_MAX);
    const hdri = (Array.isArray(p.hdri) ? p.hdri : []).map(hdriFinish).filter((e): e is HdriFinish => !!e).slice(0, FINISH_MAX);
    if (floor.length || hdri.length) out[space] = { floor, hdri };
  }
  return out;
}

/**
 * Stage's draw for a space: one of its floor entries and one of its HDRI
 * entries, by the staging's seed (the same seed, the same draw). Nothing
 * when the space has no entries.
 */
export function pickFinish(spaceType: string, pools: FinishPools | undefined, seed: string | undefined): PlanFinish | undefined {
  const pool = pools?.[spaceType];
  if (!pool) return undefined;
  const r = rng(`${seed ?? 'stage'}#finish`);
  const floor = pool.floor.length ? pool.floor[Math.floor(r() * pool.floor.length)] : undefined;
  const hdri = pool.hdri.length ? pool.hdri[Math.floor(r() * pool.hdri.length)] : undefined;
  if (!floor && !hdri) return undefined;
  return { ...(floor ? { floor: { ...floor } } : {}), ...(hdri ? { hdri: { ...hdri } } : {}) };
}

/**
 * The photoreal settings an export goes out with: the staging's pick fills
 * what the window leaves open. The floor entry applies while no floor set is
 * chosen (`materials.textures.floor` null); the HDRI entry while no HDRI is
 * chosen (`light.hdri` null), and then lights the room with it.
 */
export function applyFinish(photo: PhotoRenderSettings, finish: PlanFinish | undefined): PhotoRenderSettings {
  let out = photo;
  if (finish?.floor && photo.materials.textures.floor === null) {
    const floor = texturePlacement(finish.floor);
    if (floor) out = { ...out, materials: { ...out.materials, textures: { ...out.materials.textures, floor } } };
  }
  if (finish?.hdri && photo.light.hdri === null) {
    const { file, strength, rotation } = finish.hdri;
    out = { ...out, light: { ...out.light, world: 'hdri', hdri: file, hdriStrength: strength, hdriRotation: rotation } };
  }
  return out;
}
